import * as React from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';

// Barre d'actions groupées : n'apparaît que quand des lignes sont cochées, et
// flotte en bas de l'écran pour rester atteignable quel que soit le défilement.
interface BulkActionBarProps {
  count: number;
  onClear: () => void;
  children: React.ReactNode;
}

export function BulkActionBar({ count, onClear, children }: BulkActionBarProps) {
  const { t } = useTranslation();
  if (count === 0) return null;
  return (
    <div
      role="toolbar"
      aria-label={t('dataTable.bulkActions')}
      className={cn(
        'fixed bottom-6 left-1/2 z-40 -translate-x-1/2 sm:left-[calc(50%+2rem)]',
        'flex max-w-[calc(100vw-2rem)] items-center gap-1 overflow-x-auto rounded-lg border border-white/[0.14] bg-popover/95 p-1.5 shadow-2xl shadow-black/50 backdrop-blur rim-strong',
        'animate-in fade-in-0 slide-in-from-bottom-2 duration-150 motion-reduce:animate-none',
      )}
    >
      <span className="whitespace-nowrap px-3 text-sm text-foreground">
        {t('dataTable.selectedCount', { count })}
      </span>
      <span aria-hidden className="h-5 w-px bg-white/[0.12]" />
      {children}
      <button
        type="button"
        onClick={onClear}
        aria-label={t('dataTable.clearSelection')}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-white/[0.06] hover:text-foreground"
      >
        <X size={16} />
      </button>
    </div>
  );
}

export function BulkAction({ className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-3 text-sm font-medium text-foreground hover:bg-white/[0.06] disabled:opacity-50 [&_svg]:size-4',
        className,
      )}
      {...props}
    />
  );
}
